import {
  DEFAULT_LIMITS,
  validateDimensions,
  type DocumentLimits,
} from '../editor/core/model';

export const MAX_AUTHENTICATED_RASTER_BYTES = 25 * 1024 * 1024;
export const MAX_INTERACTIVE_RASTER_DECODED_BYTES = 64 * 1024 * 1024;

export type SafeRasterContentType = 'image/jpeg' | 'image/png' | 'image/webp';

export interface RasterSafetyLimits {
  maxBytes?: number;
  maxDimension?: number;
  maxDecodedBytes?: number;
}

export interface RasterHeader {
  contentType: SafeRasterContentType;
  width: number;
  height: number;
}

export type RasterBitmapDecoder = (
  source: Blob,
  options: ImageBitmapOptions,
) => Promise<ImageBitmap>;

export interface DecodeRasterOptions extends RasterSafetyLimits {
  decoder?: RasterBitmapDecoder;
}

const SAFE_CONTENT_TYPES = new Set<SafeRasterContentType>([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;
const JPEG_START_OF_FRAME_MARKERS = new Set([
  0xc0, 0xc1, 0xc2, 0xc3,
  0xc5, 0xc6, 0xc7,
  0xc9, 0xca, 0xcb,
  0xcd, 0xce, 0xcf,
]);

function safetyDocumentLimits(limits: RasterSafetyLimits): DocumentLimits {
  return {
    ...DEFAULT_LIMITS,
    ...(limits.maxDimension === undefined ? {} : { maxDimension: limits.maxDimension }),
    maxDecodedBytes: limits.maxDecodedBytes ?? MAX_INTERACTIVE_RASTER_DECODED_BYTES,
  };
}

function assertDimensions(width: number, height: number, limits: RasterSafetyLimits): void {
  validateDimensions(width, height, safetyDocumentLimits(limits));
}

function normalizedContentType(value: string | null): SafeRasterContentType {
  const contentType = value?.split(';', 1)[0]?.trim().toLowerCase() ?? '';
  if (!SAFE_CONTENT_TYPES.has(contentType as SafeRasterContentType)) {
    throw new Error('Maipo Cloud returned an unsupported raster content type.');
  }
  return contentType as SafeRasterContentType;
}

function isPrefix(bytes: Uint8Array, prefix: readonly number[]): boolean {
  return prefix.every((value, index) => bytes[index] === value);
}

function pngHasAnimationControl(bytes: Uint8Array): boolean {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset: number = PNG_SIGNATURE.length;
  while (offset + 12 <= bytes.length) {
    const chunkLength = view.getUint32(offset, false);
    const chunkEnd = offset + 12 + chunkLength;
    if (!Number.isSafeInteger(chunkEnd) || chunkEnd > bytes.length) return false;
    if (ascii(bytes, offset + 4, 4) === 'acTL') return true;
    offset = chunkEnd;
  }
  return false;
}

function pngDimensions(bytes: Uint8Array): { width: number; height: number } | null {
  if (!isPrefix(bytes, PNG_SIGNATURE)) return null;
  if (
    bytes.length < 24
    || bytes[8] !== 0x00
    || bytes[9] !== 0x00
    || bytes[10] !== 0x00
    || bytes[11] !== 0x0d
    || bytes[12] !== 0x49
    || bytes[13] !== 0x48
    || bytes[14] !== 0x44
    || bytes[15] !== 0x52
  ) {
    throw new Error('Maipo Cloud returned a malformed PNG header.');
  }
  if (pngHasAnimationControl(bytes)) {
    throw new Error('Maipo Cloud returned an animated PNG; only static raster images are accepted.');
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return {
    width: view.getUint32(16, false),
    height: view.getUint32(20, false),
  };
}

function jpegDimensions(bytes: Uint8Array): { width: number; height: number } | null {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let offset = 2;
  while (offset < bytes.length) {
    if (bytes[offset] !== 0xff) {
      offset += 1;
      continue;
    }
    while (bytes[offset] === 0xff) offset += 1;
    if (offset >= bytes.length) break;
    const marker = bytes[offset] ?? 0;
    offset += 1;

    if (marker === 0xd9 || marker === 0xda) break;
    if (marker === 0x01 || marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd7)) continue;
    if (offset + 2 > bytes.length) break;

    const segmentLength = ((bytes[offset] ?? 0) << 8) | (bytes[offset + 1] ?? 0);
    if (segmentLength < 2 || offset + segmentLength > bytes.length) {
      throw new Error('Maipo Cloud returned a malformed JPEG header.');
    }
    if (JPEG_START_OF_FRAME_MARKERS.has(marker)) {
      if (segmentLength < 7) throw new Error('Maipo Cloud returned a malformed JPEG frame.');
      return {
        height: ((bytes[offset + 3] ?? 0) << 8) | (bytes[offset + 4] ?? 0),
        width: ((bytes[offset + 5] ?? 0) << 8) | (bytes[offset + 6] ?? 0),
      };
    }
    offset += segmentLength;
  }
  throw new Error('Maipo Cloud returned a JPEG without readable dimensions.');
}

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(offset, offset + length));
}

function uint24LittleEndian(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0)
    | ((bytes[offset + 1] ?? 0) << 8)
    | ((bytes[offset + 2] ?? 0) << 16);
}

function webpDimensions(bytes: Uint8Array): { width: number; height: number } | null {
  if (ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WEBP') return null;
  if (bytes.length < 20) throw new Error('Maipo Cloud returned a malformed WebP header.');

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const declaredLength = view.getUint32(4, true) + 8;
  if (declaredLength !== bytes.length) {
    throw new Error('Maipo Cloud returned a WebP with an invalid container length.');
  }

  let offset = 12;
  while (offset + 8 <= bytes.length) {
    const chunkType = ascii(bytes, offset, 4);
    const chunkLength = view.getUint32(offset + 4, true);
    const payload = offset + 8;
    const chunkEnd = payload + chunkLength;
    if (!Number.isSafeInteger(chunkEnd) || chunkEnd > bytes.length) {
      throw new Error('Maipo Cloud returned a malformed WebP chunk.');
    }

    if (chunkType === 'VP8X') {
      if (chunkLength < 10) throw new Error('Maipo Cloud returned a malformed WebP VP8X header.');
      if (((bytes[payload] ?? 0) & 0x02) !== 0) {
        throw new Error('Maipo Cloud returned an animated WebP; only static raster images are accepted.');
      }
      return {
        width: uint24LittleEndian(bytes, payload + 4) + 1,
        height: uint24LittleEndian(bytes, payload + 7) + 1,
      };
    }
    if (chunkType === 'VP8L') {
      if (chunkLength < 5 || bytes[payload] !== 0x2f) {
        throw new Error('Maipo Cloud returned a malformed lossless WebP header.');
      }
      const packed = view.getUint32(payload + 1, true);
      return {
        width: (packed & 0x3fff) + 1,
        height: ((packed >>> 14) & 0x3fff) + 1,
      };
    }
    if (chunkType === 'VP8 ') {
      if (
        chunkLength < 10
        || bytes[payload + 3] !== 0x9d
        || bytes[payload + 4] !== 0x01
        || bytes[payload + 5] !== 0x2a
      ) {
        throw new Error('Maipo Cloud returned a malformed lossy WebP header.');
      }
      return {
        width: view.getUint16(payload + 6, true) & 0x3fff,
        height: view.getUint16(payload + 8, true) & 0x3fff,
      };
    }
    offset = chunkEnd + (chunkLength % 2);
  }
  throw new Error('Maipo Cloud returned a WebP without readable dimensions.');
}

export function inspectRasterHeader(
  bytes: Uint8Array,
  declaredContentType: string,
  limits: RasterSafetyLimits = {},
): RasterHeader {
  const contentType = normalizedContentType(declaredContentType);
  const png = pngDimensions(bytes);
  const jpeg = png ? null : jpegDimensions(bytes);
  const webp = png || jpeg ? null : webpDimensions(bytes);
  const detected = png
    ? { contentType: 'image/png' as const, ...png }
    : jpeg
      ? { contentType: 'image/jpeg' as const, ...jpeg }
      : webp
        ? { contentType: 'image/webp' as const, ...webp }
        : null;
  if (!detected) throw new Error('Maipo Cloud returned bytes that are not a supported raster image.');
  if (detected.contentType !== contentType) {
    throw new Error('Maipo Cloud raster bytes do not match their declared content type.');
  }
  assertDimensions(detected.width, detected.height, limits);
  return detected;
}

function advertisedLength(response: Response, maxBytes: number): void {
  const value = response.headers.get('content-length');
  if (value === null) return;
  if (!/^\d+$/.test(value)) throw new Error('Maipo Cloud returned an invalid raster Content-Length.');
  const length = Number(value);
  if (!Number.isSafeInteger(length) || length > maxBytes) {
    throw new Error(`Maipo Cloud raster exceeds the ${String(maxBytes)} byte safety limit.`);
  }
  if (length === 0) throw new Error('Maipo Cloud returned an empty raster asset.');
}

export async function readValidatedRasterResponse(
  response: Response,
  limits: RasterSafetyLimits = {},
): Promise<Blob> {
  const maxBytes = limits.maxBytes ?? MAX_AUTHENTICATED_RASTER_BYTES;
  if (!Number.isSafeInteger(maxBytes) || maxBytes < 1) throw new Error('Raster byte limit must be a positive safe integer.');
  const contentType = normalizedContentType(response.headers.get('content-type'));
  advertisedLength(response, maxBytes);
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Maipo Cloud returned an empty raster response.');

  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (true) {
      const next = await reader.read();
      if (next.done) break;
      const chunk = next.value;
      total += chunk.byteLength;
      if (!Number.isSafeInteger(total) || total > maxBytes) {
        await reader.cancel('Raster response exceeded its byte safety limit.').catch(() => undefined);
        throw new Error(`Maipo Cloud raster exceeds the ${String(maxBytes)} byte safety limit.`);
      }
      chunks.push(chunk);
    }
  } finally {
    reader.releaseLock();
  }
  if (total === 0) throw new Error('Maipo Cloud returned an empty raster asset.');

  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  const header = inspectRasterHeader(bytes, contentType, limits);
  return new Blob([bytes.buffer], { type: header.contentType });
}

export async function decodeValidatedRasterBitmap(
  blob: Blob,
  options: DecodeRasterOptions = {},
): Promise<ImageBitmap> {
  const maxBytes = options.maxBytes ?? MAX_AUTHENTICATED_RASTER_BYTES;
  if (blob.size === 0) throw new Error('Raster image is empty.');
  if (blob.size > maxBytes) throw new Error(`Raster image exceeds the ${String(maxBytes)} byte safety limit.`);

  const bytes = new Uint8Array(await blob.arrayBuffer());
  inspectRasterHeader(bytes, blob.type, options);
  const decoder = options.decoder
    ?? ((source: Blob, bitmapOptions: ImageBitmapOptions) => createImageBitmap(source, bitmapOptions));
  const bitmap = await decoder(blob, {
    colorSpaceConversion: 'default',
    premultiplyAlpha: 'none',
  });
  try {
    assertDimensions(bitmap.width, bitmap.height, options);
    return bitmap;
  } catch (error) {
    bitmap.close();
    throw error;
  }
}
